import { memo, useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { FileText, Edit2, Trash2, Plus, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Button, Input, Label } from '@caruso/ui';

interface FormField {
  id: string;
  label: string;
  variableName: string; // Auto-generated, non-editable
  type: 'text' | 'email' | 'phone' | 'number' | 'date' | 'select' | 'textarea';
  required: boolean;
  options?: string[]; // For select fields
  placeholder?: string;
}

interface FormNodeData {
  title?: string;
  description?: string;
  fields?: FormField[];
  submitButtonText?: string;
  expirationHours?: number;
}

export const FormNode = memo(({ data, id }: NodeProps<FormNodeData>) => {
  const { deleteElements, setNodes, getNodes } = useReactFlow();
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(data.title || '');
  const [description, setDescription] = useState(data.description || '');
  const [fields, setFields] = useState<FormField[]>(data.fields || []);
  const [submitButtonText, setSubmitButtonText] = useState(data.submitButtonText || 'Enviar');
  const [expirationHours, setExpirationHours] = useState(data.expirationHours || 24);

  // Função para normalizar texto para nome de variável
  const normalizeVariableName = (text: string): string => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/[^a-z0-9]/g, '_') // Substitui caracteres especiais por _
      .replace(/_+/g, '_') // Remove underscores duplicados
      .replace(/^_|_$/g, ''); // Remove underscores do início e fim
  };

  // Função para obter todos os nomes de variáveis usados no fluxo
  const getAllUsedVariableNames = (): Set<string> => {
    const usedNames = new Set<string>();
    const allNodes = getNodes();

    allNodes.forEach((node) => {
      if (node.type === 'form' && node.data.fields) {
        node.data.fields.forEach((field: FormField) => {
          if (field.variableName) {
            usedNames.add(field.variableName);
          }
        });
      }
    });

    return usedNames;
  };

  // Função para gerar nome de variável único
  const generateUniqueVariableName = (label: string, currentFieldId: string): string => {
    const baseNameRaw = normalizeVariableName(label);
    if (!baseNameRaw) return '';

    const baseName = `form:${baseNameRaw}`;
    const usedNames = getAllUsedVariableNames();

    // Remove o nome da variável atual da lista de usados (caso esteja editando)
    const currentField = fields.find((f) => f.id === currentFieldId);
    if (currentField?.variableName) {
      usedNames.delete(currentField.variableName);
    }

    // Se o nome base não está em uso, retorna ele
    if (!usedNames.has(baseName)) {
      return baseName;
    }

    // Senão, adiciona _N sequencial
    let counter = 1;
    let uniqueName = `${baseName}_${counter}`;
    while (usedNames.has(uniqueName)) {
      counter++;
      uniqueName = `${baseName}_${counter}`;
    }

    return uniqueName;
  };

  const handleDelete = () => {
    deleteElements({ nodes: [{ id }] });
  };

  const handleSave = () => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              title,
              description,
              fields,
              submitButtonText,
              expirationHours,
            },
          };
        }
        return node;
      })
    );
    setIsEditing(false);
  };

  const addField = () => {
    const newField: FormField = {
      id: `field-${Date.now()}`,
      label: '',
      variableName: '',
      type: 'text',
      required: false,
      placeholder: '',
    };
    setFields([...fields, newField]);
  };

  const removeField = (fieldId: string) => {
    setFields(fields.filter((f) => f.id !== fieldId));
  };

  const updateField = (fieldId: string, updates: Partial<FormField>) => {
    setFields(
      fields.map((f) => {
        if (f.id === fieldId) {
          const updatedField = { ...f, ...updates };

          // Se o label mudou, gera novo nome de variável
          if (updates.label !== undefined && updates.label !== f.label) {
            updatedField.variableName = generateUniqueVariableName(updates.label, fieldId);
          }

          return updatedField;
        }
        return f;
      })
    );
  };

  const fieldTypeLabels: Record<FormField['type'], string> = {
    text: 'Texto',
    email: 'Email',
    phone: 'Telefone',
    number: 'Número',
    date: 'Data',
    select: 'Seleção',
    textarea: 'Texto Longo',
  };

  return (
    <>
      <div className="bg-blue-500/10 border-2 border-blue-500 rounded-lg p-4 min-w-[280px] shadow-lg">
        <Handle type="target" position={Position.Top} className="!bg-blue-500" />

        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-bold text-blue-600">Formulário</div>
              <div className="text-xs text-gray-500">Coleta dados do cliente</div>
            </div>
          </div>

          <div className="flex gap-1">
            <button
              onClick={() => setIsEditing(true)}
              className="p-1.5 hover:bg-blue-100 rounded transition-colors"
              title="Editar"
            >
              <Edit2 className="w-4 h-4 text-blue-600" />
            </button>
            <button
              onClick={handleDelete}
              className="p-1.5 hover:bg-red-100 rounded transition-colors"
              title="Excluir"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          {title && (
            <div className="font-medium text-gray-700 truncate">
              {title}
            </div>
          )}

          {fields.length > 0 ? (
            <div className="text-xs text-gray-600">
              {fields.length} campo{fields.length !== 1 ? 's' : ''} configurado{fields.length !== 1 ? 's' : ''}
            </div>
          ) : (
            <div className="text-xs text-blue-600 italic">
              ⚠️ Configure os campos do formulário
            </div>
          )}

          {expirationHours && (
            <div className="text-xs text-gray-500">
              Expira em {expirationHours}h
            </div>
          )}
        </div>

        <Handle type="source" position={Position.Bottom} className="!bg-blue-500" />
      </div>

      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="bg-brand-ink border-brand-gold/20 text-brand-ivory min-w-[765px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-brand-gold flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Configurar Formulário
            </DialogTitle>
            <DialogDescription className="text-brand-ivory/60">
              Configure o formulário que será enviado ao cliente
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-brand-ivory/80">
                  Título do Formulário <span className="text-red-400">*</span>
                </Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ex: Cadastro de Cliente"
                  className="bg-brand-ink/50 border-brand-gold/20 text-brand-ivory mt-1"
                />
              </div>

              <div>
                <Label className="text-brand-ivory/80">
                  Expiração (horas)
                </Label>
                <Input
                  type="number"
                  value={expirationHours}
                  onChange={(e) => setExpirationHours(parseInt(e.target.value) || 24)}
                  min={1}
                  max={168}
                  className="bg-brand-ink/50 border-brand-gold/20 text-brand-ivory mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-brand-ivory/80">Descrição</Label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descreva o objetivo do formulário..."
                rows={2}
                className="w-full bg-brand-ink/50 border border-brand-gold/20 text-brand-ivory mt-1 resize-none rounded px-3 py-2"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-brand-ivory/80">
                  Campos do Formulário <span className="text-red-400">*</span>
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addField}
                  className="border-brand-gold/20 text-brand-ivory hover:bg-brand-gold/10"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Campo
                </Button>
              </div>

              {fields.length === 0 ? (
                <div className="text-center p-8 bg-brand-gold/5 rounded border border-brand-gold/20 text-brand-ivory/60">
                  Nenhum campo adicionado. Clique em "Adicionar Campo" para começar.
                </div>
              ) : (
                <div className="space-y-3">
                  {fields.map((field, index) => (
                    <div
                      key={field.id}
                      className="p-4 bg-brand-gold/5 rounded border border-brand-gold/20 space-y-3"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-brand-ivory/80">
                          Campo #{index + 1}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeField(field.id)}
                          className="text-red-400 hover:bg-red-500/10"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-brand-ivory/70 text-xs">
                            Rótulo do Campo
                          </Label>
                          <Input
                            value={field.label}
                            onChange={(e) =>
                              updateField(field.id, { label: e.target.value })
                            }
                            placeholder="Ex: Nome Completo"
                            className="bg-brand-ink/50 border-brand-gold/20 text-brand-ivory mt-1"
                          />
                        </div>

                        <div>
                          <Label className="text-brand-ivory/70 text-xs">
                            Nome da Variável
                          </Label>
                          <Input
                            value={field.variableName || ''}
                            readOnly
                            disabled
                            placeholder="Gerado automaticamente"
                            className="bg-brand-ink/30 border-brand-gold/10 text-brand-ivory/60 mt-1 cursor-not-allowed"
                            title="Gerado automaticamente baseado no rótulo do campo"
                          />
                        </div>

                        <div>
                          <Label className="text-brand-ivory/70 text-xs">
                            Tipo de Campo
                          </Label>
                          <select
                            value={field.type}
                            onChange={(e) =>
                              updateField(field.id, { type: e.target.value as FormField['type'] })
                            }
                            className="w-full bg-brand-ink/50 border border-brand-gold/20 text-brand-ivory mt-1 rounded px-3 py-2 h-10"
                          >
                            {Object.entries(fieldTypeLabels).map(([value, label]) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <Label className="text-brand-ivory/70 text-xs">
                            Placeholder (opcional)
                          </Label>
                          <Input
                            value={field.placeholder || ''}
                            onChange={(e) =>
                              updateField(field.id, { placeholder: e.target.value })
                            }
                            placeholder="Ex: Digite seu nome"
                            className="bg-brand-ink/50 border-brand-gold/20 text-brand-ivory mt-1"
                          />
                        </div>

                        <div className="col-span-2 flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`required-${field.id}`}
                            checked={field.required}
                            onChange={(e) =>
                              updateField(field.id, { required: e.target.checked })
                            }
                            className="w-4 h-4"
                          />
                          <Label
                            htmlFor={`required-${field.id}`}
                            className="text-brand-ivory/70 text-xs cursor-pointer"
                          >
                            Campo obrigatório
                          </Label>
                        </div>
                      </div>

                      {field.type === 'select' && (
                        <div>
                          <Label className="text-brand-ivory/70 text-xs">
                            Opções (uma por linha)
                          </Label>
                          <textarea
                            value={(field.options || []).join('\n')}
                            onChange={(e) =>
                              updateField(field.id, {
                                options: e.target.value.split('\n').filter(Boolean),
                              })
                            }
                            placeholder="Opção 1&#10;Opção 2&#10;Opção 3"
                            rows={3}
                            className="w-full bg-brand-ink/50 border border-brand-gold/20 text-brand-ivory mt-1 resize-none rounded px-3 py-2"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label className="text-brand-ivory/80">Texto do Botão de Envio</Label>
              <Input
                value={submitButtonText}
                onChange={(e) => setSubmitButtonText(e.target.value)}
                placeholder="Ex: Enviar Cadastro"
                className="bg-brand-ink/50 border-brand-gold/20 text-brand-ivory mt-1"
              />
            </div>

            <div className="p-4 bg-brand-gold/5 rounded border border-brand-gold/20">
              <p className="text-sm text-brand-ivory/80 mb-2 font-medium">
                ℹ️ Como funciona:
              </p>
              <ul className="text-xs text-brand-ivory/60 space-y-1 list-disc list-inside">
                <li>O cliente receberá um link único para preencher o formulário</li>
                <li>As respostas serão salvas automaticamente no contexto do fluxo</li>
                <li>O nome da variável é gerado automaticamente a partir do rótulo do campo (ex: "Nome Completo" → <code className="bg-brand-ink/50 px-1 rounded">form:nome_completo</code>)</li>
                <li>Use as variáveis nas mensagens seguintes: <code className="bg-brand-ink/50 px-1 rounded">{'{{form:nome_completo}}'}</code></li>
                <li>Se houver campos com o mesmo nome, um número sequencial será adicionado (_1, _2, etc.)</li>
                <li>Consulte as respostas em "Fluxos → Respostas Formulários"</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button
              variant="outline"
              onClick={() => setIsEditing(false)}
              className="border-brand-gold/20 text-brand-ivory hover:bg-brand-gold/10"
            >
              Cancelar
            </Button>
            <Button
              variant="premium"
              onClick={handleSave}
              disabled={!title || fields.length === 0 || fields.some((f) => !f.label)}
              className="bg-blue-500 hover:bg-blue-600"
            >
              Salvar Formulário
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

FormNode.displayName = 'FormNode';
